import { api } from '../api/client';
import { API_COLLECTIONS_URL, API_USERS_SEARCH_URL, API_USERS_URL } from '../utils';

/**
 * User lookup for sharing surfaces, over the privacy-preserving search API:
 * anyone can be found by their EXACT email address; prefix search (3+ chars)
 * only matches people the caller already collaborates with. Results carry the
 * public projection plus email: {_id, firstName, lastName, fullName, avatar,
 * email}.
 */
export async function searchUsersByEmail(email) {
	const { data } = await api.get(API_USERS_SEARCH_URL, { params: { email } });
	return data || [];
}

export async function searchCollaborators(q) {
	const { data } = await api.get(API_USERS_SEARCH_URL, { params: { q } });
	return data || [];
}

/**
 * Batch public-projection fetch (max 200 ids) — resolves names/avatars for
 * ids already on hand (group members, grant principals, picker values).
 */
export async function getUsersByIds(ids) {
	const clean = [...new Set((ids || []).filter(Boolean).map(String))];
	if (!clean.length) return [];
	// The API caps a batch at 200 ids — chunk instead of silently failing.
	const chunks = [];
	for (let i = 0; i < clean.length; i += 200) chunks.push(clean.slice(i, i + 200));
	const results = await Promise.all(
		chunks.map(chunk => api.get(API_USERS_URL, { params: { userIds: chunk.join(',') } }).then(r => r.data || []))
	);
	return results.flat();
}

/**
 * The collaborators of one collection — who a `user` element's picker lists
 * first (docs/plans/element-sets-and-me.md): owner ∪ user-grant principals ∪
 * group members ∪ the caller, public projection, name-sorted. Collection-
 * LEVEL read: a card-scoped grant answers 403, which the picker treats as
 * "nobody to list" and falls back to exact-email search.
 */
export async function getCollectionCollaborators(collectionId) {
	const { data } = await api.get(`${API_COLLECTIONS_URL}/${collectionId}/collaborators`);
	return Array.isArray(data) ? data : [];
}

/**
 * `me` in one collection: `{ user, item }` — the caller's public projection
 * and their LINKED ITEM there (the item whose account link names them), or
 * null when no card carries a link or no item names them.
 */
export async function getCollectionMe(collectionId, { cardId = null } = {}) {
	// `card_id` narrows the item to a lookup card's family — the record a
	// card-lookup's "Me" button and `options.defaultToMe` fill in.
	const query = cardId ? `?card_id=${encodeURIComponent(cardId)}` : '';
	const { data } = await api.get(`${API_COLLECTIONS_URL}/${collectionId}/me${query}`);
	return data && typeof data === 'object' ? { user: data.user || null, item: data.item || null } : { user: null, item: null };
}
